import { useEffect } from "react";
import { WS_URL, WS_RETRIES } from "../../constants/ws-constants";

/**
 * Local Websocket component for plugin manager enrolment messages for views that need to enrol cards
 * @param {sendEnrolMessage} sendEnrolMessage the message to send to the WS server
 * @param {onMessage} onMessage override for websocket message responses
 * @param {onOpen} onOpen override for websocket open
 * @param {onClosed} onClosed override for websocket closed
 * @param {onError} onError override for websocket errors
 * @param {url} url websocket url
 */
const WSPluginService = ({ onMessage, onOpen, onClosed, onError, url }) => {
    let retry = 0; // Track connection attempts
    let timeout = WS_RETRIES.TIMEOUT_DEFAULT; // timeout duration
    let wsInstance: WebSocket | undefined;

    /**
     * Determine whether the url is for Plugin Manager or not.
     * Plugin Manager does not require the access_token to be appended 
     * @param url 
     */
    const isPluginManagerWs = (url:string) => {
        return (url.includes(WS_URL.SECURED_PLUGIN_MANAGER_WEBSOCKET) || url.includes(WS_URL.UNSECURED_PLUGIN_MANAGER_WEBSOCKET));
    }

    /**
     * @function connect
     * establishes the connection with the ws and ensures constant reconnection if connection closes
     */
    const connect = () => {
        // readyState 3 = CLOSED, 2 = CLOSING, 1 = OPEN, 0 = CONNECTING
        if (wsInstance === undefined || wsInstance.readyState === WebSocket.CLOSED && retry < WS_RETRIES.MAX_RETRY) {

            // Up the retry count and increase the timeout each time we try to connect.
            // These will be reset once a successful connection is made.
            retry = retry + 1;
            timeout = timeout + WS_RETRIES.TIMEOUT_INCREASE;

            // Plugin Manager does not require authentication. So skip appending the token if it's a Plugin Manager url.
            const token = (isPluginManagerWs(url) ? '' : `?access_token=${sessionStorage.getItem("token") !== null ? JSON.parse(sessionStorage.getItem("token")!) : ''}`);
            wsInstance = new WebSocket(url+token)
            // if we fail at this point then the WS server is not running,
            // only recovery here is to reload view (if the WS server is started again only, otherwise there is still no point)
        }
        // Maximum retries exceeded. Don't keep trying to connect. Just fills the logs.
        else if (retry >= WS_RETRIES.MAX_RETRY) {
            console.log(`Failed to connect to websocket. Exceeded maximum retry attempts of `, WS_RETRIES.MAX_RETRY);
        }

        var connectInterval;

        // websocket onopen event listener
        wsInstance.onopen = () => {
            retry = 0; // Reset retries back to 0 after successful connection.
            timeout = WS_RETRIES.TIMEOUT_DEFAULT; // Reset timeout back to default after successful connection.
            clearTimeout(connectInterval); // clear Interval on open of WS
            onOpenWS(true);
        };

        // ws onclose event listener
        wsInstance.onclose = (e) => {
            console.log(
                `Socket is closed. Reconnect will be attempted in ${Math.min(10000 / 1000, timeout / 1000)} second.`,
                e.reason
            );

            if (isPluginManagerWs(url) || sessionStorage.getItem("token") !== null) {
                connectInterval = setTimeout(check, Math.min(10000, timeout)); // call check function after timeout
                onClosedWS(e);
                onOpenWS(false); // Set to false in case someone is listening
            }
        };

        // ws onerror event listener
        wsInstance.onerror = (error) => {
            console.error("Socket encountered error: ", error, "Closing socket");
            onErrorWS(error);
            wsInstance?.close();
        };

        // ws onerror event listener
        wsInstance.onmessage = (msg) => {
            onMessageReceived(msg.data);
            return false;
        };
    };

    /**
     * used by the connect function to check if the connection is close, if so attempts to reconnect
     * this only works if there was a connection to begin with and the WS server is actually running
     */
    const check = () => {
        //check if websocket instance is closed, if so call `connect` function.
        if (wsInstance === undefined || wsInstance.readyState === WebSocket.CLOSED) connect();
    };

    /**
     * websocket events that can be used by client
     */
    const onMessageReceived = (value) => {
        if (onMessage) {
            // decode data
            // onMessage(decodeResult(JSON.parse(value)));
            onMessage(value);
        }
    };

    const onOpenWS = (val) => {
        if (onOpen) {
            onOpen(wsInstance, val);
        }
    };

    const onClosedWS = (e) => {
        if (onClosed) {
            onClosed(e);
        }
    };

    const onErrorWS = (error) => {
        if (onError) {
            onError(error);
        } else {
            // TODO: when proper toast is done, throw here
            console.error(error);
        }
    };

    /**
     * only once component is mounted attempt to connect
     */
    useEffect(() => {
        // dont keep connected on every update to the view, only if undefined
        if (
            wsInstance === undefined ||
            (wsInstance.readyState !== undefined &&
                wsInstance.readyState !== WebSocket.CONNECTING &&
                wsInstance.readyState !== WebSocket.OPEN)
        ) {
            connect();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wsInstance]);

    return (null); // nothing to show
};

export default WSPluginService;
